import { ApiService } from './api-services/ApiService';
import { logger } from '../utils/logger';

interface FrontendMetrics {
  bundleSize: string;
  renderTime: number;
  loadTime: number;
  errorCount: number;
  memoryUsage?: number;
  networkRequests: number;
  timestamp: Date;
}

interface PerformanceData {
  renderTime: number;
  loadTime: number;
  errorCount: number;
  memoryUsage?: number;
}

/**
 * Service pour collecter et envoyer les métriques frontend au backend
 */
export class MetricsService {
  private static instance: MetricsService;
  private errorCount = 0;
  private networkRequests = 0;
  private lastSentTime = 0;
  private readonly SEND_INTERVAL = 30000; // 30 secondes

  private constructor() {
    this.initializeMetricsCollection();
  }

  public static getInstance(): MetricsService {
    if (!MetricsService.instance) {
      MetricsService.instance = new MetricsService();
    }
    return MetricsService.instance;
  }

  /**
   * Initialise la collecte automatique de métriques
   */
  private initializeMetricsCollection(): void {
    // Écouter les erreurs
    window.addEventListener('error', () => {
      this.errorCount++;
      this.updateStoredMetrics();
    });

    // Écouter les erreurs de promesses non gérées
    window.addEventListener('unhandledrejection', () => {
      this.errorCount++;
      this.updateStoredMetrics();
    });

    // Envoyer les métriques périodiquement (désactivé temporairement)
    // setInterval(() => {
    //   this.sendMetricsToBackend();
    // }, this.SEND_INTERVAL);

    // Envoyer les métriques avant fermeture de page (désactivé temporairement)
    // window.addEventListener('beforeunload', () => {
    //   this.sendMetricsToBackend(true);
    // });
  }

  /**
   * Enregistre une requête réseau
   */
  public recordNetworkRequest(): void {
    this.networkRequests++;
    this.updateStoredMetrics();
  }

  /**
   * Enregistre une erreur
   */
  public recordError(): void {
    this.errorCount++;
    this.updateStoredMetrics();
  }

  /**
   * Obtient les métriques de performance actuelles
   */
  public getPerformanceMetrics(): PerformanceData | null {
    try {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (!navigation) return null;

      const renderTime = Math.round(navigation.domContentLoadedEventEnd - navigation.fetchStart);
      const loadTime = Math.round(navigation.loadEventEnd - navigation.fetchStart);
      
      let memoryUsage: number | undefined;
      if ('memory' in performance) {
        const memory = (performance as { memory: { usedJSHeapSize: number } }).memory;
        memoryUsage = Math.round(memory.usedJSHeapSize / 1024 / 1024); // MB
      }

      return {
        renderTime,
        loadTime,
        errorCount: this.errorCount,
        memoryUsage,
      };
    } catch (error) {
      logger.error('Erreur récupération métriques performance:', error);
      return null;
    }
  }

  /**
   * Met à jour les métriques stockées dans localStorage
   */
  private updateStoredMetrics(): void {
    const metrics = this.getPerformanceMetrics();
    if (metrics) {
      localStorage.setItem('ekose_performance_metrics', JSON.stringify(metrics));
      localStorage.setItem('ekose_error_count', this.errorCount.toString());
    }
  }

  /**
   * Envoie les métriques au backend - ADMIN UNIQUEMENT
   * Les métriques ne sont envoyées que si l'utilisateur est admin
   */
  public async sendMetricsToBackend(force = false): Promise<void> {
    const now = Date.now();
    
    // Éviter d'envoyer trop fréquemment
    if (!force && (now - this.lastSentTime) < this.SEND_INTERVAL) {
      return;
    }

    try {
      // Vérifier si l'utilisateur est admin avant d'envoyer
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        logger.debug('Pas d\'utilisateur connecté - métriques non envoyées');
        return;
      }

      const user = JSON.parse(userStr);
      if (user.role !== 'ADMIN') {
        logger.debug('Utilisateur non-admin - métriques non envoyées', {
          userRole: user.role,
          userId: user.id
        });
        return;
      }

      const performanceData = this.getPerformanceMetrics();
      if (!performanceData) return;

      const metrics: FrontendMetrics = {
        bundleSize: '1.2MB',
        renderTime: performanceData.renderTime,
        loadTime: performanceData.loadTime,
        errorCount: performanceData.errorCount,
        memoryUsage: performanceData.memoryUsage,
        networkRequests: this.networkRequests,
        timestamp: new Date(),
      };

      await ApiService.getInstance().post('/api/dashboardRoutes/metrics/frontend', metrics);
      
      this.lastSentTime = now;
      logger.info('Métriques admin envoyées au backend:', {
        adminId: user.id,
        adminEmail: user.email,
        metricsCount: Object.keys(metrics).length
      });

      // Reset des compteurs après envoi réussi
      this.resetCounters();
      
    } catch (error) {
      // Si erreur 403, c'est normal pour les non-admins
      if (error instanceof Error && error.message.includes('403')) {
        logger.debug('Accès métriques refusé - utilisateur non-admin');
        return;
      }
      
      logger.warn('Échec envoi métriques au backend:', error);
    }
  }

  /**
   * Remet à zéro les compteurs après envoi
   */
  private resetCounters(): void {
    this.errorCount = 0;
    this.networkRequests = 0;
  }

  /**
   * Obtient un résumé des métriques pour le dashboard
   */
  public getMetricsSummary(): {
    bundleSize: string;
    performance: 'excellent' | 'good' | 'poor';
    errorRate: number;
    lastUpdate: Date;
  } {
    const performanceData = this.getPerformanceMetrics();
    
    let performance: 'excellent' | 'good' | 'poor' = 'poor';
    if (performanceData) {
      if (performanceData.loadTime < 2000 && performanceData.renderTime < 100) {
        performance = 'excellent';
      } else if (performanceData.loadTime < 5000 && performanceData.renderTime < 300) {
        performance = 'good';
      }
    }

    return {
      bundleSize: '1.2MB',
      performance,
      errorRate: this.errorCount,
      lastUpdate: new Date(),
    };
  }

  /**
   * Démarre le monitoring des Core Web Vitals
   */
  public startWebVitalsMonitoring(): void {
    // First Contentful Paint
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === 'first-contentful-paint') {
          logger.info(`FCP: ${entry.startTime.toFixed(2)}ms`);
        }
      }
    });

    try {
      observer.observe({ entryTypes: ['paint'] });
    } catch (error) {
      logger.warn('Web Vitals monitoring non supporté:', error);
    }
  }

  /**
   * Nettoie les ressources
   */
  public cleanup(): void {
    // Envoyer les dernières métriques
    this.sendMetricsToBackend(true);
    
    // Nettoyer localStorage
    localStorage.removeItem('ekose_performance_metrics');
    localStorage.removeItem('ekose_error_count');
  }
}

// Instance singleton
export const metricsService = MetricsService.getInstance();

export default metricsService;
